import type { UrlLocation } from '@seek/chalice-types';
import { useMelwaysLink } from '@seek/melways-react';
import {
  type CountrySiteName,
  type Language,
  type Locale,
  type createUrlResolver,
  getLocale,
  getSiteNameFromHostname,
  HOMEPAGE_LANGUAGE_OVERRIDE_PATH,
} from '@seek/melways-sites';
import {
  hrefFromLocation,
  isAbsoluteUrl,
  isHomepagePath,
  isLocalisedPath,
} from '@seek/seek-jobs-seo';
import { useMemo } from 'react';

import { useSelector } from 'src/store/react';
import { selectLanguage } from 'src/store/selectors';

const devDomainsSiteNamesMap: Record<string, CountrySiteName> = {
  'dev.seek.com.au': 'candidate-seek-au',
  'dev.seek.co.nz': 'candidate-seek-nz',
  'dev.hk.jobsdb.com': 'candidate-jobsdb-hk',
  'dev.th.jobsdb.com': 'candidate-jobsdb-th',
  'dev.jobstreet.co.id': 'candidate-jobstreet-id',
  'dev.jobstreet.com.my': 'candidate-jobstreet-my',
  'dev.jobstreet.com.ph': 'candidate-jobstreet-ph',
  'dev.jobstreet.com.sg': 'candidate-jobstreet-sg',
  'dev.au.seek.com': 'candidate-au',
  'dev.nz.seek.com': 'candidate-nz',
  'dev.hk.seek.com': 'candidate-hk',
  'dev.th.seek.com': 'candidate-th',
  'dev.id.seek.com': 'candidate-id',
  'dev.my.seek.com': 'candidate-my',
  'dev.ph.seek.com': 'candidate-ph',
  'dev.sg.seek.com': 'candidate-sg',
};

const previewDomainsSiteNamesMap: Record<string, CountrySiteName> = {
  'discover.staging.cloud.seek.com.au': 'candidate-seek-au',
  'discover.staging.cloud.seek.co.nz': 'candidate-seek-nz',
};

export const getSiteFromHostname = (
  hostname: string,
): { site: CountrySiteName; shouldAllowOverride?: boolean } => {
  try {
    return {
      site: getSiteNameFromHostname(hostname) as CountrySiteName,
      // It's safe to only check for `staging` here since the invocation ..
      // .. to `getSiteNameFromHostname` will throw if the hostname is invalid
      shouldAllowOverride: hostname.includes('staging'),
    };
  } catch (_) {
    if (devDomainsSiteNamesMap[hostname]) {
      return {
        site: devDomainsSiteNamesMap[hostname],
      };
    }

    /**
     * Preview domains aren't a one-to-one mapping of hostname to site name.
     * Instead, they start with branch name and end with one of the listed ..
     * .. domains above and that's why we need to check if the hostname includes ..
     * .. the preview domain.
     * i.e  `branch-name.discover.staging.cloud.seek.com.au`
     * */
    const matchedPreviewDomain = Object.entries(
      previewDomainsSiteNamesMap,
    ).find(([domain]) => hostname.includes(domain));

    if (matchedPreviewDomain) {
      return {
        site: matchedPreviewDomain[1],
        shouldAllowOverride: true,
      };
    }

    return { site: 'candidate-seek-au' };
  }
};

export const getLocaleFromSiteAndLanguage = ({
  language,
  site,
}: {
  language: Language;
  site: CountrySiteName;
}) =>
  getLocale(
    site,
    language,
    // melways' getLocale returns Locale | Language, although the only case it will give a language is ..
    // .. when it can't map the zone to a country, so it defaults to the language in the passed args
  ) as Locale;

export const useLocalisedUrlLocation = (location: UrlLocation): UrlLocation => {
  const localisedPath = useLocalisedLinks({ path: location.pathname || '' });

  return useMemo(() => {
    if (location.protocol === 'tel:') {
      return location;
    }

    if (localisedPath === location.pathname) {
      return location;
    }

    const localisedUrlLocation = {
      ...location,
      pathname: localisedPath,
    };

    return {
      ...localisedUrlLocation,
      href: hrefFromLocation(localisedUrlLocation, location.href),
    };
  }, [localisedPath, location]);
};

type UseLocalisedLinksProp = Parameters<
  ReturnType<typeof createUrlResolver>
>['0'];
type UseLocalisedLinkReturn<T> = T extends UseLocalisedLinksProp[]
  ? string[]
  : string;
export const useLocalisedLinks = <
  T extends UseLocalisedLinksProp | UseLocalisedLinksProp[],
>(
  props: T,
): UseLocalisedLinkReturn<T> => {
  const selectedLanguage = useSelector(selectLanguage);

  // accept both single prop object and an array of props
  const linkProps = useMemo<UseLocalisedLinksProp[]>(
    () => (Array.isArray(props) ? props : [props]),
    [props],
  );

  const localisedPaths = useMelwaysLink(
    // apply melways override for homepage path
    linkProps.map(({ path, language: lang, ...rest }) => ({
      // Temporarily, we need to pass language here to ensure homepage exception rules are considered
      // If candidates land on `/` for `th-TH` or `id-ID`, melways provider will set the `language` as `en`
      // while it should be the local language.
      language: lang ?? selectedLanguage,
      path: isHomepagePath(path) ? HOMEPAGE_LANGUAGE_OVERRIDE_PATH : path,
      ...rest,
    })),
  );

  return useMemo(() => {
    const paths = localisedPaths.map((localisedPath, index) => {
      const { path, language } = linkProps[index];
      const isLocalised = isLocalisedPath({
        path,
        language: (language || selectedLanguage) as Language,
      });
      const isAbsolute = isAbsoluteUrl(path);
      return !isLocalised && !isAbsolute ? localisedPath : path;
    });

    return Array.isArray(props) ? paths : paths[0];
  }, [
    localisedPaths,
    props,
    linkProps,
    selectedLanguage,
  ]) as UseLocalisedLinkReturn<T>;
};
